import { Card, CardHeader, CardContent, Grid, Box, Typography, Tooltip, Button, Popover, SxProps, styled } from "@mui/material";
import { FC, useState } from "react";
import ReadonlyField from "../input/ReadonlyField";
import { autoCopyTextFieldProps } from "@/utils/clipboard";
import Create from "@/controller/Create";
import WithSave from "@/controller/WithSave";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { ContainerRegistryPullPasswordDto } from "@/gen/api/securefile";
import { ShowBase, useRecordContext } from "ra-core";


const DataAnalyzerImagePull: FC = () => {
  return (
    <ShowBase resource="data-analyzer-image-pull-pass" id="info" basePath="">
      <DataAnalyzerImagePullInner />
    </ShowBase>
  )
}

const DataAnalyzerImagePullInner: FC = () => {
  const authnInfo : ContainerRegistryPullPasswordDto|undefined = useRecordContext() as any
  const [states, setStates] = useState({
    tokenPassword: null as ContainerRegistryPullPasswordDto|null,
    genBtnEl: null as Element|null,
    copied: false
  })

  const handler = useDefaultSavedHandlers("Generate new Token Password")

  const tokenNameLines = authnInfo ? [
    `TOKEN_NAME=${authnInfo?.tokenName}`,
    '',
  ] : []

  const loginCodeLines = authnInfo ? [
    `TOKEN_NAME=${authnInfo?.tokenName}`,
    'TOKEN_PWD=<token password>',
    '',
    `echo $TOKEN_PWD | docker login --username $TOKEN_NAME --password-stdin ${authnInfo?.loginServer}`,
    '',
  ] : []

  const pullCodeLines = authnInfo ? [
    `docker pull ${authnInfo.loginServer}/products/ondata_data_analyzer:latest`,
    ''
  ] : []

  return (
    <Card sx={{ overflowY: 'auto', mt: 2, mb: 4, }} >
      <CardHeader title="Pull Data Analyzer Image" />
      <CardContent>
        <Grid container spacing={2} >
          <Grid item xs={12}>
            <Box sx={{ width: '100%' }} >
              <Typography variant="caption" component="p">
                To pull the Data Analyzer container image from OnData's Container Registry, you need to login to the OnData Container Registry with a valid token name and token password. The token name is automatically generated below.
              </Typography>
              <CodeAreaDisplay lines={tokenNameLines} />
            </Box>
          </Grid>
          {!states.tokenPassword &&
            <Grid item xs={12}>
              <Typography variant="caption" component="p" paddingBottom={2} marginTop={-1}>
                Click on the button below to generate a new token password: 
              </Typography>
              <Button variant="outlined" size="small"
                onClick={(e) => setStates(v => ({...v, genBtnEl: e.target as any}))}
              >
                Generate new Token Password
              </Button>
              <Popover id={'pop-trial-run'} anchorOrigin={{ vertical: 'top', horizontal: 'left', }} transformOrigin={{ vertical: 'top', horizontal: 'left'}}
                open={!!states.genBtnEl}
                anchorEl={states.genBtnEl}
                onClose={() => setStates(v => ({...v, genBtnEl: null}))}
              >
                <Box sx={{ m: 2, width: 450 }}>
                  <Typography variant="caption" component="p">
                    Generate a new Token Password will effectively invalidate previously generated Token Passwords if any. Once the new Token Password is generated, it will be displayed for copy for only once. Please keep it at somewhere safe. The generated Token Password has a 1 year expiry time and it's only meant to be used by your organization.
                  </Typography>
                  <Create resource="data-analyzer-image-pull-pass" basePath="" {...handler}>
                    <WithSave
                      render={({save, saving}) =>
                        <Button variant="contained" size="small" fullWidth sx={{mt: 1}}
                          disabled={saving}
                          onClick={() => save(1, {
                            onSuccess: ({data}: {data:ContainerRegistryPullPasswordDto}) => {
                              setStates(v => ({...v, tokenPassword: data }))
                            },
                          })}
                        >
                          Generate
                        </Button>}
                    />
                  </Create>
                </Box>
              </Popover>
            </Grid>}
          <Grid item xs={12}>
            <Box sx={{ width: '100%' }} >
              <Typography variant="caption" component="p">
                With the token name and token password, you can login to the OnData Container Registry. Below is a sample bash script for the login. You should replace the token password value with the one generated above.
              </Typography>
              <CodeAreaDisplay lines={loginCodeLines} />
            </Box>
          </Grid>
          {states.tokenPassword &&
            <Grid item xs={12}>
              <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                <Box>
                  <ReadonlyField 
                    id="client-id-generated"
                    label="New Token Password"
                    value={states.tokenPassword.password || ''}
                    {...autoCopyTextFieldProps(
                      () => setStates(v => ({...v, copied: true})), 
                      () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                    )}
                    {...{inputProps: {tabIndex: 0}}}
                  />
                </Box>
              </Tooltip>
            </Grid>}
          <Grid item xs={12}>
            <Box sx={{ width: '100%' }} >
              <Typography variant="caption" component="p" marginTop={-1}>
                Once logged in to the OnData Container Registry, you can run the following command to pull the Data Analyzer container image.
              </Typography>
              <CodeAreaDisplay lines={pullCodeLines} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default DataAnalyzerImagePull;

const CodeAreaDisplay: FC<{lines: string[]}> = ({lines}) => {
  return (
    <CodeAreaPre>
      <code>
        {lines.map((l, i) =>
          <>
            <span className="linenumber">{i + 1}</span>
            <span>{l}</span>
            <span>{'\r\n'}</span>
          </>
        )}
      </code>
    </CodeAreaPre>
  )
}

const CodeAreaPre = styled('pre')(({theme}) => ({
  padding: theme.spacing(1),
  fontSize: '0.75em',
  backgroundColor: theme.palette.divider,
  '& span.linenumber': {
    display: 'inline-block',
    minWidth: '1.25em',
    paddingRight: '1em',
    textAlign: 'right',
    userSelect: 'none',
    color: 'rgb(98, 114, 164)',
  },
  overflowX: 'auto'
}))